import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import './adminUddannelse.css';

const AdminUddannelse = () => {
  const navigate = useNavigate();
  const textBeforeRef = useRef(null);
  const textAfterRef = useRef(null);
  const fullDescriptionRef = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [uddannelseData, setUddannelseData] = useState({
    title: '',
    heroImage: '',
    textBefore: '',
    textAfter: ''
  });
  
  const [uddannelser, setUddannelser] = useState([]);
  const [editingUddannelse, setEditingUddannelse] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Format states
  const [textBeforeFormats, setTextBeforeFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [textAfterFormats, setTextAfterFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [fullDescriptionFormats, setFullDescriptionFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load content
  useEffect(() => {
    const fetchUddannelseData = async () => {
      try {
        // Fetch uddannelse main content
        const uddannelseDocRef = doc(db, 'content', 'uddannelse');
        const uddannelseDoc = await getDoc(uddannelseDocRef);
        
        if (uddannelseDoc.exists()) {
          const data = uddannelseDoc.data();
          setUddannelseData({
            title: data.title || '',
            heroImage: data.heroImage || '',
            textBefore: data.textBefore || '',
            textAfter: data.textAfter || ''
          });
          
          if (data.heroImage) {
            setHeroPreview(data.heroImage);
          }
        }
        
        // Fetch uddannelser
        const uddannelserDocRef = doc(db, 'content', 'uddannelser');
        const uddannelserDoc = await getDoc(uddannelserDocRef);
        
        if (uddannelserDoc.exists() && uddannelserDoc.data().uddannelser) {
          setUddannelser(uddannelserDoc.data().uddannelser);
        } else {
          // Tom array hvis ingen data findes
          setUddannelser([]);
        }
      } catch (error) {
        console.error('Fejl ved hentning af uddannelsesdata:', error);
        setMessage({
          text: 'Der opstod en fejl ved indlæsning af indhold',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchUddannelseData();
    }
  }, [user]);

  // Initialisering af editorer
  useEffect(() => {
    if (isEditing) {
      if (textBeforeRef.current) {
        textBeforeRef.current.innerHTML = uddannelseData.textBefore || '';
      }
      if (textAfterRef.current) {
        textAfterRef.current.innerHTML = uddannelseData.textAfter || '';
      }
    }

    if (editingUddannelse && fullDescriptionRef.current) {
      fullDescriptionRef.current.innerHTML = editingUddannelse.fullDescription || '';
    }
  }, [isEditing, editingUddannelse, uddannelseData.textBefore, uddannelseData.textAfter]);

  // Handlere for hovedindhold
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUddannelseData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleHeroFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setHeroFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handlere for uddannelser
  const handleEditUddannelse = (uddannelse) => {
    setEditingUddannelse({...uddannelse});
    setPreview(uddannelse.imageUrl);
    setIsCreating(false);
  };

  const handleCreateUddannelse = () => {
    setEditingUddannelse({
      id: '',
      title: '',
      description: '',
      imageUrl: '',
      link: '',
      fullDescription: '',
      textBefore: '',
      textAfter: '',
      events: []
    });
    setPreview(null);
    setSelectedFile(null);
    setIsCreating(true);
  };

  const handleUddannelseInputChange = (e) => {
    const { name, value } = e.target;
    setEditingUddannelse(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteUddannelse = async (uddannelseId) => {
    if (window.confirm('Er du sikker på, at du vil slette denne uddannelse?')) {
      try {
        const uddannelse = uddannelser.find(u => u.id === uddannelseId);
        
        // Slet billedet hvis det er gemt i storage
        if (uddannelse.imageUrl && uddannelse.imageUrl.includes('firebase')) {
          try {
            // Udtryek filnavnet fra Firebase Storage URL'en korrekt
            const url = new URL(uddannelse.imageUrl);
            const pathPart = url.pathname.split('/o/')[1]; // Get everything after '/o/'
            if (pathPart) {
              const decodedPath = decodeURIComponent(pathPart.split('?')[0]); // Remove query params and decode
              const imageRef = ref(storage, decodedPath);
              await deleteObject(imageRef);
            }
          } catch (storageError) {
            console.warn('Billedet kunne ikke slettes fra storage:', storageError);
            // Fortsæt med at slette uddannelsen selvom billedet ikke kan slettes
          }
        }
        
        // Opdater array uden den slettede uddannelse
        const updatedUddannelser = uddannelser.filter(u => u.id !== uddannelseId);
        setUddannelser(updatedUddannelser);
        
        // Gem opdateret array i Firestore
        await setDoc(doc(db, 'content', 'uddannelser'), {
          uddannelser: updatedUddannelser,
          lastUpdated: new Date()
        });
        
        setMessage({ type: 'success', text: 'Uddannelse blev slettet!' });
      } catch (error) {
        console.error("Fejl ved sletning:", error);
        setMessage({ type: 'error', text: 'Fejl ved sletning af uddannelse.' });
      }
    }
  };

  const handleSaveUddannelse = async () => {
    setIsSaving(true);
    try {
      if (!editingUddannelse.id && isCreating) {
        // Generer et ID baseret på titlen hvis det er en ny uddannelse
        editingUddannelse.id = editingUddannelse.title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      }
      
      // Link baseret på ID
      if (!editingUddannelse.link) {
        editingUddannelse.link = `/uddannelse/${editingUddannelse.id}`;
      }
      
      // Sikre at der er en tom events array, hvis der ingen er
      if (!editingUddannelse.events) {
        editingUddannelse.events = [];
      }
      
      let imageUrl = editingUddannelse.imageUrl;
      
      // Upload nyt billede hvis det er valgt
      if (selectedFile) {
        const storageRef = ref(storage, `uddannelser/${editingUddannelse.id}-${Date.now()}`);
        await uploadBytes(storageRef, selectedFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      
      // Hent det formaterede indhold fra editoren direkte
      const fullDescription = fullDescriptionRef.current ? fullDescriptionRef.current.innerHTML : '';
      
      const updatedUddannelse = {
        ...editingUddannelse,
        imageUrl,
        fullDescription
      };
      
      let updatedUddannelser;
      
      if (isCreating) {
        updatedUddannelser = [...uddannelser, updatedUddannelse];
      } else {
        updatedUddannelser = uddannelser.map(u => 
          u.id === updatedUddannelse.id ? updatedUddannelse : u
        );
      }
      
      // Gem opdateret array i Firestore
      await setDoc(doc(db, 'content', 'uddannelser'), {
        uddannelser: updatedUddannelser,
        lastUpdated: new Date()
      });
      
      setUddannelser(updatedUddannelser);
      setEditingUddannelse(null);
      setSelectedFile(null);
      setPreview(null);
      setIsCreating(false);
      
      setMessage({ 
        type: 'success', 
        text: isCreating ? 'Ny uddannelse oprettet!' : 'Uddannelse opdateret!' 
      });
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ 
        type: 'error', 
        text: 'Fejl ved gem af uddannelse.' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveHovedindhold = async () => {
    setIsSaving(true);
    try {
      let heroImageUrl = uddannelseData.heroImage;
      
      // Upload nyt hero billede hvis det er valgt
      if (heroFile) {
        const storageRef = ref(storage, `uddannelse/hero-${Date.now()}`);
        await uploadBytes(storageRef, heroFile);
        heroImageUrl = await getDownloadURL(storageRef);
      }
      
      // Hent det formaterede indhold fra editorerne direkte
      const textBefore = textBeforeRef.current ? textBeforeRef.current.innerHTML : '';
      const textAfter = textAfterRef.current ? textAfterRef.current.innerHTML : '';
      
      // Gem hovedindhold i Firestore
      await setDoc(doc(db, 'content', 'uddannelse'), {
        ...uddannelseData,
        textBefore,
        textAfter,
        heroImage: heroImageUrl,
        lastUpdated: new Date()
      });
      
      setUddannelseData(prev => ({
        ...prev,
        textBefore,
        textAfter,
        heroImage: heroImageUrl
      }));
      
      setIsEditing(false);
      setHeroFile(null);
      setMessage({ type: 'success', text: 'Uddannelsessiden blev opdateret!' });
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ type: 'error', text: 'Fejl ved gem af uddannelsesside.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingUddannelse(null);
    setPreview(null);
    setSelectedFile(null);
    setIsCreating(false);
    setIsEditing(false);
    setHeroFile(null);
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  // Tilføj event listener for selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      // Kun tjek formateringer hvis vi er i redigeringstilstand
      if (!isEditing && !editingUddannelse) return;
      
      // Find ud af hvilken editor der har fokus
      const activeElement = document.activeElement;
      const isTextBefore = textBeforeRef.current && textBeforeRef.current.contains(activeElement);
      const isTextAfter = textAfterRef.current && textAfterRef.current.contains(activeElement);
      const isFullDescription = fullDescriptionRef.current && fullDescriptionRef.current.contains(activeElement);
      
      if (isTextBefore) {
        checkActiveFormats('textBefore');
      } else if (isTextAfter) {
        checkActiveFormats('textAfter');
      } else if (isFullDescription) {
        checkActiveFormats('fullDescription');
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isEditing, editingUddannelse]);

  const handleEditorFocus = (editorType) => {
    const editor = {
      textBefore: textBeforeRef.current,
      textAfter: textAfterRef.current,
      fullDescription: fullDescriptionRef.current
    }[editorType];

    if (editor) {
      editor.focus();
      checkActiveFormats(editorType);
    }
  };

  const checkActiveFormats = (editorType) => {
    const setFormats = {
      textBefore: setTextBeforeFormats,
      textAfter: setTextAfterFormats,
      fullDescription: setFullDescriptionFormats
    }[editorType];
    
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false,
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink'),
      underline: document.queryCommandState('underline')
    });
  };

  // Håndter link-indsættelse
  const handleLinkInsertion = (editor) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    // Tjek om selektionen er inden for editoren
    if (!editor || !editor.contains(range.commonAncestorContainer)) {
      return;
    }
    
    // Tjek om den valgte tekst er en URL
    const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
    
    if (url && url.trim() !== '') {
      const linkText = selectedText || prompt('Indtast link tekst:', '');
      
      if (linkText && linkText.trim() !== '') {
        // Indsæt link direkte i HTML
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.textContent = linkText;
        
        // Indsæt linket
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Flyt cursoren efter linket
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleFormatClick = (formatType, editorType) => {
    const editor = {
      textBefore: textBeforeRef.current,
      textAfter: textAfterRef.current,
      fullDescription: fullDescriptionRef.current
    }[editorType];

    if (!editor) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!editor.contains(document.activeElement)) {
      editor.focus();
    }

    // Udfør formateringen direkte
    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        if (!range) return;
        
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement && parentElement.tagName === 'H2';
        
        if (isHeading) {
          document.execCommand('formatBlock', false, 'p');
        } else {
          document.execCommand('formatBlock', false, 'h2');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        handleLinkInsertion(editor);
        break;
      
      case 'underline':
        document.execCommand('underline', false);
        break;
      
      default:
        break;
    }

    // Opdater aktive formateringer
    checkActiveFormats(editorType);
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-uddannelse-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til Admin
          </button>
          
          <h1>Administrer Uddannelse</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Hovedindhold på Uddannelsessiden</h2>
              <button 
                className="edit-content-btn"
                onClick={() => setIsEditing(true)}
                disabled={isEditing}
              >
                Rediger indhold
              </button>
            </div>
            
            {isEditing ? (
              <div className="uddannelse-form">
                <div className="form-group">
                  <label htmlFor="title">Overskrift</label>
                  <input 
                    type="text" 
                    id="title" 
                    name="title"
                    value={uddannelseData.title || ''}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="image-uploader">
                  <label>Hovedbillede</label>
                  
                  {heroPreview && (
                    <div className="image-preview hero-preview">
                      <img src={heroPreview} alt="Forhåndsvisning" />
                    </div>
                  )}
                  
                  <label className="upload-btn">
                    {heroPreview ? 'Skift billede' : 'Vælg billede'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleHeroFileChange}
                      id="hero-image-upload"
                    />
                  </label>
                </div>
                
                <div className="form-group">
                  <label htmlFor="textBefore">Tekst over uddannelser</label>
                  <FormatBar 
                    activeFormats={textBeforeFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'textBefore')}
                  />
                  <div
                    ref={textBeforeRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('textBefore')}
                    data-placeholder="Skriv teksten der skal vises over uddannelser..."
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="textAfter">Tekst under uddannelser</label>
                  <FormatBar 
                    activeFormats={textAfterFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'textAfter')}
                  />
                  <div
                    ref={textAfterRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('textAfter')}
                    data-placeholder="Skriv teksten der skal vises under uddannelser..."
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSaveHovedindhold}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Gemmer...' : 'Gem ændringer'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            ) : (
              <div className="content-preview">
                <p className="admin-description">
                  Her kan du redigere hovedindholdet på uddannelsessiden, inklusiv overskrift, billede og tekster.
                </p>
                <p>Klik på "Rediger indhold" for at ændre hovedindholdet.</p>
              </div>
            )}
          </div>
          
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Uddannelser</h2>
              {!editingUddannelse && (
                <button 
                  className="create-btn"
                  onClick={handleCreateUddannelse}
                >
                  + Opret ny uddannelse
                </button>
              )}
            </div>
            
            <p className="admin-description">
              Her kan du administrere uddannelser og kurser der vises på uddannelsessiden.
              <br />For at administrere events for en uddannelse, klik først "Rediger" og derefter "Administrer Events".
            </p>
            
            {editingUddannelse ? (
              <div className="uddannelse-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="uddannelse-title">Titel</label>
                    <input 
                      type="text" 
                      id="uddannelse-title" 
                      name="title"
                      value={editingUddannelse.title || ''}
                      onChange={handleUddannelseInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="uddannelse-link">Link</label>
                    <input 
                      type="text" 
                      id="uddannelse-link" 
                      name="link"
                      value={editingUddannelse.link || ''}
                      onChange={handleUddannelseInputChange}
                    />
                    <span className="form-help-text">
                      Fx. '/uddannelse/mbt-specialist' - Lad være tom for at generere automatisk
                    </span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="uddannelse-description">Kort beskrivelse</label>
                  <textarea 
                    id="uddannelse-description" 
                    name="description"
                    rows="3"
                    value={editingUddannelse.description || ''}
                    onChange={handleUddannelseInputChange}
                    required
                  ></textarea>
                  <span className="form-help-text">
                    Kort beskrivelse der vises på kortet på uddannelsessiden
                  </span>
                </div>
                
                <div className="image-uploader">
                  <label>Billede</label>
                  
                  {preview && (
                    <div className="image-preview">
                      <img src={preview} alt="Forhåndsvisning" />
                    </div>
                  )}
                  
                  <label className="upload-btn">
                    {preview ? 'Skift billede' : 'Vælg billede'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileChange}
                      id="image-upload"
                    />
                  </label>
                  
                  {!isCreating && !selectedFile && (
                    <span className="form-help-text">
                      Lad være tom for at beholde eksisterende billede
                    </span>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="uddannelse-fullDescription">Fuld beskrivelse (til undersiden)</label>
                  <FormatBar 
                    activeFormats={fullDescriptionFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'fullDescription')}
                  />
                  <div
                    ref={fullDescriptionRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('fullDescription')}
                    data-placeholder="Skriv den fulde beskrivelse der vises på uddannelsens egen side..."
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSaveUddannelse}
                    disabled={isSaving || !editingUddannelse.title || !editingUddannelse.description || (isCreating && !preview)}
                  >
                    {isSaving ? 'Gemmer...' : isCreating ? 'Opret uddannelse' : 'Gem ændringer'}
                  </button>
                  {!isCreating && (
                    <button 
                      className="admin-uddannelse-events-btn"
                      onClick={() => navigate(`/admin/uddannelse/${editingUddannelse.id}/events`)}
                    >
                      Administrer Events
                    </button>
                  )}
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            ) : (
              <>
                {uddannelser.length > 0 ? (
                  <div className="uddannelse-list">
                    {uddannelser.map((uddannelse) => (
                      <div key={uddannelse.id} className="uddannelse-item">
                        <div className="uddannelse-preview">
                          <div className="uddannelse-image">
                            <img src={uddannelse.imageUrl} alt={uddannelse.title} />
                          </div>
                          <div className="uddannelse-content">
                            <h3>{uddannelse.title}</h3>
                            <p>{uddannelse.description}</p>
                            <a className="uddannelse-link" href={uddannelse.link} target="_blank" rel="noopener noreferrer">
                              Vis side
                            </a>
                            <div className="uddannelse-event-count">
                              <span>{uddannelse.events?.length || 0} events</span>
                            </div>
                          </div>
                        </div>
                        <div className="uddannelse-actions">
                          <button 
                            className="admin-uddannelse-edit-btn"
                            onClick={() => handleEditUddannelse(uddannelse)}
                          >
                            Rediger
                          </button>
                          <button 
                            className="admin-uddannelse-events-btn"
                            onClick={() => navigate(`/admin/uddannelse/${uddannelse.id}/events`)}
                          >
                            Events
                          </button>
                          <button 
                            className="admin-uddannelse-delete-btn"
                            onClick={() => handleDeleteUddannelse(uddannelse.id)}
                          >
                            Slet
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-items">
                    <p>Ingen uddannelser fundet. Opret en ny uddannelse for at komme i gang.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminUddannelse;
